/**
 * useUatDecision.ts — approve / push back a deployment in UAT
 * (ADR-075 steps 3–4, scenario-app-uat-gate)
 *
 * POST /api/broker/v1/apps/:app/deployments/:n/approve
 * POST /api/broker/v1/apps/:app/deployments/:n/pushback   ({ comment } required)
 *
 * The SPA-side role guard is UX only — the API verifies the uat-approver
 * realm role server-side (403) and rejects decisions on rows that are not in
 * the active UAT slot (409). Those errors are surfaced as human-readable,
 * non-technical states.
 */

import { useState } from 'react';

export type UatDecision = 'approve' | 'pushback';

export type UatDecisionState =
  | { tag: 'idle' }
  | { tag: 'submitting'; decision: UatDecision }
  | { tag: 'done'; decision: UatDecision }
  | { tag: 'error'; message: string };

interface DecideArgs {
  appId: string;
  deployNo: number;
  decision: UatDecision;
  comment?: string;
}

export function useUatDecision(token: string | null) {
  const [state, setState] = useState<UatDecisionState>({ tag: 'idle' });

  async function decide({ appId, deployNo, decision, comment }: DecideArgs): Promise<boolean> {
    if (!token) {
      setState({ tag: 'error', message: 'not-authenticated' });
      return false;
    }
    setState({ tag: 'submitting', decision });
    try {
      const res = await fetch(
        `/api/broker/v1/apps/${encodeURIComponent(appId)}/deployments/${deployNo}/${decision}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(comment ? { comment } : {}),
        },
      );
      if (res.ok) {
        setState({ tag: 'done', decision });
        return true;
      }
      // Map API failures to i18n-key-shaped reasons (rendered by the panel).
      const reason =
        res.status === 403 ? 'forbidden' :
        res.status === 409 ? 'stale' :
        res.status === 400 ? 'invalid' : 'failed';
      setState({ tag: 'error', message: reason });
      return false;
    } catch {
      setState({ tag: 'error', message: 'network' });
      return false;
    }
  }

  return { state, decide, reset: () => setState({ tag: 'idle' }) };
}
